import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import PageLayout from '@/components/feature/PageLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import { mangaList, categories } from '@/mocks/manga';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredManga = useMemo(() => {
    if (!slug || slug === 'all') return mangaList;
    return mangaList.filter((m) => {
      const cat = categories.find((c) => c.slug === slug);
      return cat ? m.category === cat.name : true;
    });
  }, [slug]);

  const categoryTitle = slug === 'all' ? '全部漫画' : `${activeCategory.name}漫画`;

  return (
    <PageLayout>
      <SEOHead
        title={categoryTitle}
        description={`虫虫漫画${categoryTitle}分类，提供最新最全的${activeCategory.name}类免费在线漫画阅读。海量${activeCategory.name}漫画资源，每日同步更新。`}
        keywords={`${activeCategory.name}漫画,免费${activeCategory.name}漫画,在线${activeCategory.name}漫画,虫虫漫画${activeCategory.name}`}
        canonicalUrl={`https://www.chongchongmanga.com/category/${slug}`}
      />

      <div className="pt-16">
        {/* Category Header */}
        <section className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryTitle },
              ]}
            />
            <div className="pb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {categoryTitle}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredManga.length} 部{activeCategory.name}漫画作品
              </p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200 sticky top-14 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    (slug || 'all') === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Manga Grid */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {filteredManga.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                {filteredManga.map((manga) => (
                  <MangaCard key={manga.id} manga={manga} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 bg-background-200 rounded-full flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
                <Link
                  to="/category/all"
                  className="inline-flex mt-4 text-primary-600 hover:text-primary-700 text-sm"
                >
                  查看全部漫画
                </Link>
              </div>
            )}
          </div>
        </section>
      </div>
    </PageLayout>
  );
}